import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import ConversationDto from '@/models/Conversations/ConversationDto';
import store from '@/store';
import { doc, getDocs, onSnapshot, limit, query, collection, setDoc, addDoc, where, orderBy, QueryFieldFilterConstraint } from "firebase/firestore";
import db from '@/firebase/init';
import MessageDto from '@/models/Messages/MessageDto';
import WorkerProfileDto from '@/models/Workers/WorkerProfileDto';
import { AppModule } from './app';
import FilterConversationSearchViewModel from '@/models/Conversations/FilterConversationSearchViewModel';
import CurrentConversationSearchCriteria from '@/models/Conversations/CurrentConversationSearchCriteria';
import UserProfileDto from '@/models/Users/UserProfileDto';
import ConversationTopicFirestoreDto from '@/models/ConversationTopics/ConversationTopicFirestoreDto';
import EntityType from '@/models/Shared/EntityType';
import ConversationParticipantDto from '@/models/Conversations/ConversationParticipantDto';
import BookingRoleWorkerDto from '@/models/BookingRoleWorkers/BookingRoleWorkerDto';
import AgencyUserDto from '@/models/AgencyUsers/AgencyUserDto';
import AssignConversationRequest from '@/models/Conversations/AssignConversationRequest';
import CreateConversationParticipantRequest from '@/models/Conversations/CreateConversationParticipantRequest';
import UpdateConversationStatusRequest from '@/models/Conversations/UpdateConversationStatusRequest';
import UserType from '@/models/Users/UserType';

export interface IMessagingState {
    loadingConversations: boolean;
    loadingConversation: boolean;
    loadingDialog: boolean;
    loadingWorkerProfile: boolean;
    loadingBookingRoleWorkerSummary: boolean;
    showNewConversationBanner: boolean;
    filterConversationSearchViewModel: FilterConversationSearchViewModel;
    currentConversationSearchCriteria: CurrentConversationSearchCriteria;
    conversationTopics: ConversationTopicFirestoreDto[];
    selectedConversationTopic: ConversationTopicFirestoreDto;
    totalActionRequiredConversationsCount: string;
    conversations: ConversationDto[];
    selectedConversation: ConversationDto;
    messages: MessageDto[];
    workerProfile: WorkerProfileDto;
    bookingRoleWorkerSummary: BookingRoleWorkerDto;
    unsubscribeToConversationTopics: any;
    unsubscribeToConversations: any;
    unsubscribeToMessages: any;
    conversationParticipants: ConversationParticipantDto[];
    unsubscribeToConversationParticipants: any;
    agencyUsers: AgencyUserDto[];
  }

@Module({ dynamic: true, store, name: 'MessagingModule', namespaced: true })
class MessagingStore extends VuexModule implements IMessagingState {

    public loadingConversations = true;
    public loadingConversation = true;
    public loadingDialog = false;
    public loadingWorkerProfile = true;
    public loadingBookingRoleWorkerSummary = true;
    public showNewConversationBanner = false;
    public filterConversationSearchViewModel: FilterConversationSearchViewModel = new FilterConversationSearchViewModel();
    public currentConversationSearchCriteria: CurrentConversationSearchCriteria = new CurrentConversationSearchCriteria();
    public conversationTopics: ConversationTopicFirestoreDto[] = [];
    public selectedConversationTopic: ConversationTopicFirestoreDto = null;
    public totalActionRequiredConversationsCount = "0";
    public conversations: ConversationDto[] = [];
    public selectedConversation: ConversationDto = null;
    public messages: MessageDto[] = [];
    public workerProfile: WorkerProfileDto = new WorkerProfileDto();
    public bookingRoleWorkerSummary: BookingRoleWorkerDto = new BookingRoleWorkerDto();
    public unsubscribeToConversationTopics: any = null;
    public unsubscribeToConversations: any = null;
    public unsubscribeToMessages: any = null;
    public conversationParticipants: ConversationParticipantDto[] = [];
    public unsubscribeToConversationParticipants: any = null;
    public agencyUsers: AgencyUserDto[] = [];

    @Action 
    async mapUserDefaults() {
      const userProfile = AppModule.userProfile;
      this.context.commit("setUserDefaults", userProfile);
    }

    @Action
    public async getAgencyUsers() {
      await ajax.get(`/api/v1/agency/users`)
      .then(response => {
        this.context.commit("setAgencyUsers", response.data );
      })
      .catch(error => {
        AppModule.handleApiError(error);
      });
    }
  
    @Action
    async getConversationTopics() {
        let q;
        if (AppModule.userProfile.userType === UserType.SysAdmin) {
            this.context.commit("clearConversationTopics");
            q = query(collection(db, 
                "tenants/" + AppModule.currentAgencyContext.id + "/conversationTopics"), 
                orderBy("name"));
        } else {
        
            if (AppModule.userProfile.conversationTopicIds == null || AppModule.userProfile.conversationTopicIds == undefined) {
                this.setLoadingConversations(false);
                return;
            }
            if (AppModule.userProfile.conversationTopicIds.length == 0) {
                this.setLoadingConversations(false);
                return;
            }

            this.context.commit("clearConversationTopics");
            q = query(collection(db, 
                "tenants/" + AppModule.currentAgencyContext.id + "/conversationTopics"), 
                orderBy("name"),
                where("__name__", "in", AppModule.userProfile.conversationTopicIds));
        }
        const unsubscribeToConversationTopicsCallback = onSnapshot(q, (querySnapshot: { docChanges: () => any[] }) => {
            querySnapshot.docChanges().forEach((change: { type: string; doc: { id: string; data: () => { (): any; new(): any; name: string; actionRequiredCount: number; isEnabled: boolean; isPublic: boolean } } }) => {
                if (change.type === "added") {
                    const conversationTopic = new ConversationTopicFirestoreDto();
                    conversationTopic.id = change.doc.id;
                    conversationTopic.name = change.doc.data().name;
                    conversationTopic.actionRequiredCount = change.doc.data().actionRequiredCount;
                    conversationTopic.isEnabled = change.doc.data().isEnabled;
                    conversationTopic.isPublic = change.doc.data().isPublic;
                    this.context.commit("addConversationTopic", conversationTopic);
                }
                if (change.type === "modified") {
                    //Find the conversation and update the values
                    const conversationTopic = new ConversationTopicFirestoreDto();
                    conversationTopic.id = change.doc.id;
                    conversationTopic.actionRequiredCount = change.doc.data().actionRequiredCount;
                    conversationTopic.isEnabled = change.doc.data().isEnabled;
                    conversationTopic.isPublic = change.doc.data().isPublic;
                    this.context.commit("updateConversationTopic", conversationTopic);
                }
                if (change.type === "removed") {
                    //This is not possible
                }
            });
        });

        this.context.commit("setUnsubscribeToConversationTopicsCallback", unsubscribeToConversationTopicsCallback);
       // this.setLoadingConversations(false);
    }

    @Action
    async getConversations(pageNumber: number) {

        if (!this.selectedConversationTopic) {
            return;
        }
        this.setLoadingConversations(true);
        this.context.commit("clearConversations");


        //this.currentConversationSearchCriteria = new CurrentConversationSearchCriteria();
        this.currentConversationSearchCriteria.selectedAssignedTo = this.filterConversationSearchViewModel.selectedAssignedTo;
        this.currentConversationSearchCriteria.selectedStatus = this.filterConversationSearchViewModel.selectedStatus;
        //debugger;
        //let filter = new Array<QueryFieldFilterConstraint>();
        // if (this.filterConversationViewModel.selectedBranch !== undefined) {
        //     filter.push(where("branchId", "==", this.filterConversationViewModel.selectedBranch.id));
        // }
        // if (this.filterConversationViewModel.selectedDesk !== undefined) {
        //     filter.push(where("deskId", "==", this.filterConversationViewModel.selectedDesk.id));
        // }
        // if (this.filterConversationViewModel.selectedConversationTopic !== undefined) {
        //     filter.push(where("conversationTopicId", "==", this.filterConversationViewModel.selectedConversationTopic.id));
        // }
        let q;
        if (this.currentConversationSearchCriteria.selectedStatus !== undefined) {
            if (this.currentConversationSearchCriteria.selectedAssignedTo !== undefined) {
                //both are defined
                //console.log("********** path 1 ************")
                q = query(
                    collection(db, 
                        "tenants/" + AppModule.currentAgencyContext.id + "/conversationTopics/" + this.selectedConversationTopic.id + "/conversations"), 
                        orderBy("lastMessageCreatedAt", "desc"), 
                        where("status", "==", this.filterConversationSearchViewModel.selectedStatus), 
                        where("assignedToFirebaseUserId", "==", this.filterConversationSearchViewModel.selectedAssignedTo.userId));
            } else {
                //console.log("********** path 2 ************")
                q = query(
                collection(db, 
                    "tenants/" + AppModule.currentAgencyContext.id + "/conversationTopics/" + this.selectedConversationTopic.id + "/conversations"), 
                    orderBy("lastMessageCreatedAt", "desc"), 
                    where("status", "==", this.filterConversationSearchViewModel.selectedStatus));
            }
        } else {
            if (this.currentConversationSearchCriteria.selectedAssignedTo !== undefined) {
                //console.log("********** path 3 ************")
                q = query(
                    collection(db, 
                        "tenants/" + AppModule.currentAgencyContext.id + "/conversationTopics/" + this.selectedConversationTopic.id + "/conversations"), 
                        // orderBy("status"), 
                        orderBy("lastMessageCreatedAt", "desc"), 
                        where("assignedToFirebaseUserId", "==", this.filterConversationSearchViewModel.selectedAssignedTo.userId),
                        where("status", "in",[10,20,30,40,50]));
            } else {
                //console.log("********** path 4 ************")
            q = query(
                collection(db, 
                    "tenants/" + AppModule.currentAgencyContext.id + "/conversationTopics/" + this.selectedConversationTopic.id + "/conversations"), 
                    // orderBy("status"), 
                    orderBy("lastMessageCreatedAt", "desc"), 
                    where("status", "in",[10,20,30,40,50]));
                }
        }
        //console.log(q);
        const unsubscribeToConversationsCallback = onSnapshot(q, (querySnapshot: { docChanges: () => any[] }) => {
            querySnapshot.docChanges().forEach((change: { type: string; doc: { id: string; data: () => { (): any; new(): any; branchId: string; deskId: string; locationId: string; locationBrandName: string; companyId: string; companyBrandName: string; departmentId: string; departmentName: string; conversationTopicId: string; conversationTopicType: number; 
                workerId: string; workerFirebaseUserId: string; workerDisplayName: string; workerAvatar: string; assignedToEntityId: string; assignedToEntityType: number; assignedToFirebaseUserId: string; assignedToDisplayName: string; assignedToAvatar: string; bookingId: string; bookingRoleId: string; bookingRoleWorkerId: string; sequenceNumber: string;
                lastMessageCreatedAt: any; lastMessage: MessageDto; conversationTopicName: string;
                participantFirebaseUserIds: string[]; status: number; subject: string; }; }; }) => {
                if (change.type === "added") {
                    const conversation = new ConversationDto();
                    conversation.id = change.doc.id;
                    conversation.branchId = change.doc.data().branchId;
                    conversation.deskId = change.doc.data().deskId;
                    conversation.bookingId = change.doc.data().bookingId;
                    conversation.bookingRoleId = change.doc.data().bookingRoleId;
                    conversation.bookingRoleWorkerId = change.doc.data().bookingRoleWorkerId;
                    conversation.companyId = change.doc.data().companyId;
                    conversation.companyBrandName = change.doc.data().companyBrandName;
                    conversation.locationId = change.doc.data().locationId;
                    conversation.locationBrandName = change.doc.data().locationBrandName;
                    conversation.departmentId = change.doc.data().departmentId;
                    conversation.departmentName = change.doc.data().departmentName;
                    conversation.workerFirebaseUserId = change.doc.data().workerFirebaseUserId;
                    conversation.workerAvatar = change.doc.data().workerAvatar;
                    conversation.workerDisplayName = change.doc.data().workerDisplayName;
                    conversation.workerId = change.doc.data().workerId;
                    conversation.assignedToEntityId = change.doc.data().assignedToEntityId;
                    conversation.assignedToEntityType = change.doc.data().assignedToEntityType;
                    conversation.assignedToFirebaseUserId = change.doc.data().assignedToFirebaseUserId;
                    conversation.assignedToDisplayName = change.doc.data().assignedToDisplayName;
                    conversation.assignedToAvatar = change.doc.data().assignedToAvatar;
                    conversation.participantFirebaseUserIds = change.doc.data().participantFirebaseUserIds;
                    conversation.subject = change.doc.data().subject;
                    conversation.status = change.doc.data().status;
                    conversation.sequenceNumber = change.doc.data().sequenceNumber;
                    conversation.conversationTopicId = change.doc.data().conversationTopicId;
                    conversation.conversationTopicType = change.doc.data().conversationTopicType;
                    conversation.conversationTopicName = change.doc.data().conversationTopicName;
                    conversation.lastMessageCreatedAt = change.doc.data().lastMessageCreatedAt.toDate();
                    conversation.lastMessage = change.doc.data().lastMessage;
                    
                    if (this.conversations.length === 0) {
                        //First conversation in the list
                        //console.log("first conversation detected. Last message created at = " + conversation.lastMessageCreatedAt);
                        this.context.commit("addConversation", conversation);
                    } else if (conversation.lastMessageCreatedAt > this.conversations[0].lastMessageCreatedAt) {
                        //A new conversation has been added after the initial set has been loaded
                        //We don't add one here as it disrupts the selected conversation and the user 
                        //could be typing a message 
                        //console.log("new conversation detected");
                        //console.log("Conversation last message created at = " + conversation.lastMessageCreatedAt);
                        //console.log("First conversation last message created at = " + this.conversations[0].lastMessageCreatedAt);
                        this.context.commit("setShowNewConversationBanner", true);
                    } else {
                        //Conversation is older than the first in the list - add it to the end
                        //console.log("other conversation detected");
                        this.context.commit("addConversation", conversation);
                    }
                    if (!this.selectedConversation) {
                          this.updateSelectedConversation(conversation);
                    }
                    
                }
                if (change.type === "modified") {
                    const existingConversationIndex = this.conversations.findIndex((findConversation: ConversationDto) => findConversation.id === change.doc.id);
                    if (existingConversationIndex != -1) {
                        this.conversations[existingConversationIndex].status = change.doc.data().status;
                        this.conversations[existingConversationIndex].conversationTopicName = change.doc.data().conversationTopicName;
                        this.conversations[existingConversationIndex].assignedToDisplayName = change.doc.data().assignedToDisplayName;
                        this.conversations[existingConversationIndex].assignedToAvatar = change.doc.data().assignedToAvatar;
                        this.conversations[existingConversationIndex].assignedToEntityId = change.doc.data().assignedToEntityId;
                        this.conversations[existingConversationIndex].assignedToEntityType = change.doc.data().assignedToEntityType;
                        this.conversations[existingConversationIndex].participantFirebaseUserIds = change.doc.data().participantFirebaseUserIds;
                        this.conversations[existingConversationIndex].lastMessageCreatedAt = change.doc.data().lastMessageCreatedAt.toDate();
                        this.conversations[existingConversationIndex].lastMessage = change.doc.data().lastMessage;
                    }
                }
                if (change.type === "removed") {
                    const conversationToBeRemovedIndex = this.conversations.findIndex((findConversation: ConversationDto) => findConversation.id === change.doc.id);
                    if (conversationToBeRemovedIndex != -1) {
                        this.context.commit("removeConversation", conversationToBeRemovedIndex);
                        this.context.commit("clearMessages");
                        this.context.commit("clearConversationParticipants");
                    }
                    //If it was the last one in the list and there are still conversations left
                    //select the one above it
                    if (this.conversations.length > 0) {
                        if (this.conversations.length === conversationToBeRemovedIndex) {
                            //It was the last one in the index
                            //Select the one above it
                            this.updateSelectedConversation(this.conversations[conversationToBeRemovedIndex -1]);
                        } else {
                            //It wasn't the last one - replace with the one that took its place
                            this.updateSelectedConversation(this.conversations[conversationToBeRemovedIndex]);
                        }
                        
                    }
                }
            });
        });
        this.context.commit("setUnsubscribeToConversationsCallback", unsubscribeToConversationsCallback);
        this.setLoadingConversations(false);
    }

    @Action
    public async updateSelectedConversationTopic(conversationTopic: ConversationTopicFirestoreDto){
        this.context.commit("setSelectedConversationTopic", conversationTopic);
    }

    @Action
    public async updateSelectedConversation(conversation: ConversationDto){
        this.context.commit("setSelectedConversation", conversation);
        this.setLoadingConversation(true);
        
        const messagesQuery = query(
            collection(db, 
                "tenants/" + AppModule.currentAgencyContext.id + "/conversationTopics/" + conversation.conversationTopicId + "/conversations/" + this.selectedConversation.id + "/conversationMessages"), orderBy("createdAt"));

        //const querySnapshot = await getDocs(q);
        //var results: MessageDto[] = [];
        //const numberChildren = querySnapshot.size;
        
        //this.setTotalConversationsPages(numberChildren);
        // querySnapshot.forEach(((doc: any) => {
        //     const msg = new MessageDto();
        //     msg.id = doc.id;
        //     msg.body = doc.data().body;
        //     msg.userType = doc.data().userType;
        //     msg.createdAt = doc.data().createdAt.toDate();
        //     msg.createdByDisplayName = doc.data().createdByDisplayName;
        //     results.push(msg);
        // }))

        const unsubscribeToMessagesCallback = onSnapshot(messagesQuery, (querySnapshot: { docChanges: () => any[] }) => {
            querySnapshot.docChanges().forEach((change: { type: string; doc: { id: string; data: () => { (): any; new(): any; createdAt: any; firebaseUserId: string; body: string; hasWorkerRead: boolean; type: number} } }) => {
                const msg = new MessageDto();
                msg.id = change.doc.id;
                msg.body = change.doc.data().body;
                msg.firebaseUserId = change.doc.data().firebaseUserId;
                msg.type = change.doc.data().type;
                msg.createdAt = change.doc.data().createdAt.toDate();
                msg.hasWorkerRead = change.doc.data().hasWorkerRead;
                if (change.type === "added") {
                    this.context.commit("addMessage", msg);
                }
                if (change.type === "modified") {
                    this.context.commit("updateMessage", msg);
                }
                if (change.type === "removed") {
                }
            });
        });
        this.context.commit("setUnsubscribeToMessagesCallback", unsubscribeToMessagesCallback);

        const conversationParticipantsQuery = query(
            collection(db, 
                "tenants/" + AppModule.currentAgencyContext.id + "/conversationTopics/" + conversation.conversationTopicId + "/conversations/" + this.selectedConversation.id + "/conversationParticipants"), orderBy("displayName"));
  
        //const conversationParticipantsQuerySnapshot = await getDocs(conversationParticipantsQuery);
        //var results: ConversationParticipantDto[] = [];
        //const numberChildren = querySnapshot.size;
        
        //this.setTotalConversationsPages(numberChildren);
        // querySnapshot.forEach(((doc: any) => {
        //     const msg = new MessageDto();
        //     msg.id = doc.id;
        //     msg.body = doc.data().body;
        //     msg.userType = doc.data().userType;
        //     msg.createdAt = doc.data().createdAt.toDate();
        //     msg.createdByDisplayName = doc.data().createdByDisplayName;
        //     results.push(msg);
        // }))
  
        const unsubscribeToConversationParticipantsCallback = onSnapshot(conversationParticipantsQuery, (querySnapshot: { docChanges: () => any[] }) => {
            querySnapshot.docChanges().forEach((change: { type: string; doc: { id: string; data: () => { (): any; new(): any; displayName: string; entityId: string; avatar: string; entityType: EntityType} } }) => {
                if (change.type === "added") {
                    const conversationParticipant = new ConversationParticipantDto();
                    conversationParticipant.id = change.doc.id;
                    conversationParticipant.avatar = change.doc.data().avatar;
                    conversationParticipant.entityType = change.doc.data().entityType as EntityType;
                    conversationParticipant.entityId = change.doc.data().entityId;
                    conversationParticipant.displayName = change.doc.data().displayName;
                    this.context.commit("addConversationParticipant", conversationParticipant);
                }
                if (change.type === "modified") {
                }
                if (change.type === "removed") {
                }
            });
            
        });
        this.context.commit("setUnsubscribeToConversationParticipantsCallback", unsubscribeToConversationParticipantsCallback);

        this.setLoadingConversation(false);
      
    }

    @Action
    public async sendMessage(message: string){
        // const newMessage = new MessageDto();
        // newMessage.createdAt = new Date();
        // newMessage.createdByDisplayName = AppModule.currentUser.displayName;
        // newMessage.createdByFirebaseUserId = AppModule.currentUser.uid;
        // newMessage.createdByEntityType = EntityType.AgencyUser;
        // newMessage.body = message;
        const newMessage = {
            "createdAt": new Date(),
            "tenantId": AppModule.currentAgencyContext.id,
            "firebaseUserId": AppModule.currentUser.uid,
            "hasWorkerRead" : false,
            "type": 1,
            "body": message
        }

        await addDoc(collection(db, "tenants/" + AppModule.currentAgencyContext.id + "/conversationTopics/" + this.selectedConversation.conversationTopicId + "/conversations/" + this.selectedConversation.id + "/conversationMessages"), newMessage);
    }

    @Action
    public async assignConversation(request: AssignConversationRequest) {
      await ajax.put(`/api/v1/agency/conversation-topics/${this.selectedConversation.conversationTopicId}/conversations/${this.selectedConversation.id}/assigned-to`, request)
      .then(response => {

      })
      .catch(error => {
        AppModule.handleApiError(error);
      });
    }

    @Action
    public async editConversationStatus(request: UpdateConversationStatusRequest) {
      await ajax.put(`/api/v1/agency/conversation-topics/${this.selectedConversation.conversationTopicId}/conversations/${this.selectedConversation.id}/status`, request)
      .then(response => {

      })
      .catch(error => {
        AppModule.handleApiError(error);
      });
    }

    @Action
    public async createConversationParticipant(request: CreateConversationParticipantRequest) {
      await ajax.post(`/api/v1/agency/conversation-topics/${this.selectedConversation.conversationTopicId}/conversations/${this.selectedConversation.id}/participants`, request)
      .then(response => {

      })
      .catch(error => {
        AppModule.handleApiError(error);
      });
    }

    @Action
    public async getWorkerProfile(id: string) {
      this.setLoadingWorkerProfile(true); 
      await ajax.get(`/api/v1/agency/workers/${id}/profile`)
      .then(response => {
        this.context.commit("setWorkerProfile", response.data);
        this.setLoadingWorkerProfile(false);
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingWorkerProfile(false);
      })
    }

    @Action
    public async getBookingRoleWorkerSummary(id: string) {
      this.setLoadingBookingRoleWorkerSummary(true); 
      //This is silly - we aren't checking the booking or booking role ID so it stil works
      await ajax.get(`/api/v1/agency/bookings/null-id/roles/null-id/workers/${id}`)
      .then(response => {
        this.context.commit("setBookingRoleWorkerSummary", response.data);
        //this.setLoadingBookingRoleWorkerSummary(false);
      })
      .catch(error => {
        AppModule.handleApiError(error);
        //this.setLoadingBookingRoleWorkerSummary(false);
      })
    }

    @Action
    public async cleanState(retainConversationTopics: boolean) {
        this.context.commit("resetState", retainConversationTopics);
    }

    @Mutation
    resetState(retainConversationTopics: boolean) {
        this.loadingConversations = true;
        this.loadingConversation = false;
        this.loadingDialog = false;
        this.loadingWorkerProfile = false;
        this.loadingBookingRoleWorkerSummary = false;
        this.filterConversationSearchViewModel = new FilterConversationSearchViewModel();
        this.selectedConversation = null;
        if (!retainConversationTopics) {
            this.conversationTopics = [];
            this.selectedConversationTopic = null;
            this.conversations = [];
            if (this.unsubscribeToConversationTopics) {
                this.unsubscribeToConversationTopics();
                //this.unsubscribeToConversations = null;
            }
        }
        if (this.unsubscribeToConversations) {
            this.unsubscribeToConversations();
            //this.unsubscribeToConversations = null;
        }
        this.messages = [];
        if (this.unsubscribeToMessages) {
            this.unsubscribeToMessages();
            //this.unsubscribeToMessages = null;
        }
        this.conversationParticipants = [];
        if (this.unsubscribeToConversationParticipants) {
            this.unsubscribeToConversationParticipants();
        }
        this.workerProfile = new WorkerProfileDto();
        this.agencyUsers = [];
    }

    @Mutation 
    private async setUserDefaults(userProfile: UserProfileDto) {
    //   var branch: BranchDto = null;
    //   if (userProfile.defaultBranchId !== null) {
    //     //Loop through each branch until we find the correct one
    //     branch = AgencySettingsModule.branches.find((findBranch: BranchDto) => findBranch.id === userProfile.defaultBranchId);
    //     if (branch != undefined) {
    //       this.filterConversationViewModel.selectedBranch = branch;
    //     }
    //   }
    //   if (userProfile.defaultDeskId !== null) {
    //     //Loop through each desk until we find the correct one
    //     if (branch !== undefined)
    //     var desk = branch.desks.find((findDesk: DeskDto) => findDesk.id === userProfile.defaultDeskId);
    //     if (desk !== undefined) {
    //       this.filterConversationViewModel.selectedDesk = desk
    //     }
    //}
    }

    @Mutation
    private async setLoadingWorkerProfile(loading: boolean) {
        this.loadingWorkerProfile = loading;
    }

    @Mutation
    private async setLoadingBookingRoleWorkerSummary(loading: boolean) {
        this.loadingBookingRoleWorkerSummary = loading;
    }

    @Mutation
    private async setLoadingDialog(loading: boolean) {
        this.loadingDialog = loading;
    }

    @Mutation
    private async setLoadingConversations(loading: boolean) {
        this.loadingConversations = loading;
    }

    @Mutation
    private async setLoadingConversation(loading: boolean) {
        this.loadingConversation = loading;
    }

    @Mutation
    private async clearConversations() {
        if (this.unsubscribeToConversations != null) {
            this.unsubscribeToConversations();
            this.unsubscribeToConversations = null;
        }
        this.conversations = [];
        this.selectedConversation = null;
        if (this.unsubscribeToMessages != null) {
            this.unsubscribeToMessages();
            this.unsubscribeToMessages = null;
        }
        this.messages = [];
        if (this.unsubscribeToConversationParticipants != null) {
            this.unsubscribeToConversationParticipants();
        }
        this.conversationParticipants = [];
       this.workerProfile = null;
       this.bookingRoleWorkerSummary = null;
       this.showNewConversationBanner = false;
    }

    @Mutation
    private async clearConversationTopics() {
        this.conversationTopics = [];
        if (this.unsubscribeToConversationTopics != null) {
            this.unsubscribeToConversationTopics();
            this.unsubscribeToConversationTopics = null;
        }
        this.selectedConversation = null;
        this.selectedConversationTopic = null;
    }

    @Mutation
    private async setShowNewConversationBanner(show: boolean) {
        this.showNewConversationBanner = show;
    }

    @Mutation
    private async addConversationTopic(conversationTopic: ConversationTopicFirestoreDto) {
        this.conversationTopics.push(conversationTopic);
        let count = 0;
        this.conversationTopics.forEach(topic => {
            count += topic.actionRequiredCount;
        });
        if (count > 9) {
            this.totalActionRequiredConversationsCount = "9+"
        } else {
            this.totalActionRequiredConversationsCount = count.toString();
        } 
        if (!this.selectedConversationTopic) {
            this.selectedConversationTopic = conversationTopic;
        }
    }

    @Mutation
    private async updateConversationTopic(conversationTopic: ConversationTopicFirestoreDto) {
        const foundConversationTopic = this.conversationTopics.find((findConversationTopic: ConversationTopicFirestoreDto) => findConversationTopic.id === conversationTopic.id);
        if (foundConversationTopic != null) {
            foundConversationTopic.actionRequiredCount = conversationTopic.actionRequiredCount;
        }
        let count = 0;
        this.conversationTopics.forEach(topic => {
            count += topic.actionRequiredCount;
        });
        if (count > 9) {
            this.totalActionRequiredConversationsCount = "9+"
        } else {
            this.totalActionRequiredConversationsCount = count.toString();
        }   
    }

    @Mutation
    private async addConversation(conversation: ConversationDto) {
        this.conversations.push(conversation);
    }   

    @Mutation
    private async removeConversation(conversationIndex: number) {
        const conversationByIndex = this.conversations[conversationIndex];
        if (conversationByIndex && this.selectedConversation) {
            if (conversationByIndex.id == this.selectedConversation.id) {
                this.selectedConversation = null;
            }
        }
        const deleted = this.conversations.splice(conversationIndex, 1);
    }

    @Mutation
    private async setSelectedConversation(conversation: ConversationDto) {
        if (this.unsubscribeToMessages != null) {
            this.unsubscribeToMessages();
        }
        this.messages = [];
        if (this.unsubscribeToConversationParticipants != null) {
            this.unsubscribeToConversationParticipants();
        }
        this.conversationParticipants = [];
        this.selectedConversation = conversation;
    }

    @Mutation
    private async setWorkerProfile(workerProfile: WorkerProfileDto) {
        this.workerProfile = workerProfile;
    }

    @Mutation
    private async setBookingRoleWorkerSummary(brw: BookingRoleWorkerDto) {
        this.bookingRoleWorkerSummary = brw;
    }

    @Mutation
    private async setMessages(messages: MessageDto[]) {
        this.messages = messages;
    }

    @Mutation
    private async addMessage(message: MessageDto) {
        this.messages.push(message);
    }

    @Mutation
    private async updateMessage(message: MessageDto) {
        const foundMessage = this.messages.find((findMessage: MessageDto) => findMessage.id === message.id);
        if (foundMessage != null) {
            foundMessage.hasWorkerRead = message.hasWorkerRead;
        }
    }

    @Mutation
    private async clearMessages() {
        this.messages = [];
        if (this.unsubscribeToMessages != null) {
            this.unsubscribeToMessages();
        }
    }

    @Mutation
    private async setAgencyUsers(agencyUsers: AgencyUserDto[]) {
        this.agencyUsers = agencyUsers;
    }

    @Mutation
    private async setSelectedConversationTopic(conversationTopic: ConversationTopicFirestoreDto) {
        this.selectedConversationTopic = conversationTopic;
    }

    @Mutation
    private async setConversationParticipants(conversationParticipants: ConversationParticipantDto[]) {
        this.conversationParticipants = conversationParticipants;
    }
  
    @Mutation
    private async addConversationParticipant(conversationParticipant: ConversationParticipantDto) {
        this.conversationParticipants.push(conversationParticipant);
    }
  
    @Mutation
    private async clearConversationParticipants() {
        this.conversationParticipants = [];
        if (this.unsubscribeToConversationParticipants != null) {
            this.unsubscribeToConversationParticipants();
        }
    }

    @Mutation
    private async setUnsubscribeToConversationTopicsCallback(callback: any) {
        this.unsubscribeToConversationTopics = callback;
    }

    @Mutation
    private async setUnsubscribeToConversationsCallback(callback: any) {
        this.unsubscribeToConversations = callback;
    }

    @Mutation
    private async setUnsubscribeToMessagesCallback(callback: any) {
        this.unsubscribeToMessages = callback;
    }

    @Mutation
    private async setUnsubscribeToConversationParticipantsCallback(callback: any) {
        this.unsubscribeToConversationParticipants = callback;
    }
}

export const MessagingModule = getModule(MessagingStore);